import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { RefreshCw, Send } from 'lucide-react';
import { configuracionAPI, alertasAPI } from '../services/api';
import { SecretInput } from '../utils/adminShared';
import { toPercentInput, percentToDecimal, normalizarBaseUrl } from '../utils/helpers';
import { Card, Button, Input } from './ui';

/**
 * ConfiguracionPanel
 * Parámetros operativos: tipo de cambio, IGV, rentabilidad, numeración,
 * integraciones (Google Sheets, Slack). Ruta: /configuracion.
 *
 * Los datos de empresa (logo, razón social, métodos de pago, firma) viven
 * aparte en DatosEmpresa.js (ruta /datos-empresa) porque son branding/PDF
 * y se tocan con menos frecuencia.
 */
const ConfiguracionPanel = ({ onConfigUpdate }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState({});
    const [tipoCambioInfo, setTipoCambioInfo] = useState(null);
    const [loadingTC, setLoadingTC] = useState(false);
    const [slackConfig, setSlackConfig] = useState({ webhook_url: '', activo: false, app_url: '' });
    const [loadingSlackTest, setLoadingSlackTest] = useState(false);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const [configRes, slackRes] = await Promise.all([
                configuracionAPI.getAll(),
                alertasAPI.getSlack()
            ]);
            setConfig(configRes.data || {});
            if (slackRes.data) setSlackConfig(slackRes.data);
        } catch (err) {
            console.error('Error al cargar configuración:', err);
        } finally {
            setLoading(false);
        }
    };

    // Solo actualiza estado local (sin llamar API)
    const handleLocalChange = (clave, valor) => {
        setConfig(prev => ({ ...prev, [clave]: valor }));
    };

    // Guardar inmediato (solo para SecretInput que lo requiere)
    const handleImmediateSave = async (clave, valor) => {
        try {
            await configuracionAPI.update(clave, valor);
            setConfig(prev => {
                const newConfig = { ...prev, [clave]: valor };
                if (onConfigUpdate) onConfigUpdate(newConfig);
                return newConfig;
            });
        } catch (err) {
            toast.error('Error al actualizar: ' + err.message);
        }
    };

    // Guardar parametros operativos + slack en un solo batch
    const guardarTodo = async () => {
        setSaving(true);
        try {
            const configToSave = {
                tipo_cambio: config.tipo_cambio || '3.70',
                igv_rate: config.igv_rate || '0.18',
                rentabilidad: config.rentabilidad || '0.68',
                rentabilidad_costo_fijo: config.rentabilidad_costo_fijo || '0.68',
            };

            await configuracionAPI.updateMultiple(configToSave);

            // Normalizar URL antes de guardar: agrega https:// si falta y quita
            // trailing slash. Asi el backend no concatena dobles barras al armar
            // links a cotizaciones (/cotizaciones/123 vs //cotizaciones/123).
            const appUrlNormalizada = normalizarBaseUrl(slackConfig.app_url);
            if (appUrlNormalizada !== slackConfig.app_url) {
                setSlackConfig(prev => ({ ...prev, app_url: appUrlNormalizada }));
            }
            await alertasAPI.updateSlack({
                activo: slackConfig.activo,
                app_url: appUrlNormalizada
            });

            setConfig(prev => ({ ...prev, ...configToSave }));
            if (onConfigUpdate) onConfigUpdate({ ...config, ...configToSave });
            toast.success('Configuración guardada correctamente');
        } catch (err) {
            toast.error('Error al guardar: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const actualizarTipoCambioSunat = async () => {
        setLoadingTC(true);
        try {
            const response = await configuracionAPI.getTipoCambioSunat();
            setTipoCambioInfo(response.data);
            if (response.data.venta) {
                const val = response.data.venta.toString();
                await configuracionAPI.update('tipo_cambio', val);
                const newConfig = { ...config, tipo_cambio: val };
                setConfig(newConfig);
                if (onConfigUpdate) onConfigUpdate(newConfig);
            }
            toast.success(`Tipo de cambio actualizado: ${response.data.venta} (${response.data.fuente})`);
        } catch (err) {
            toast.error('Error al obtener tipo de cambio: ' + err.message);
        } finally {
            setLoadingTC(false);
        }
    };

    if (loading) {
        return <div className="loading">Cargando configuración...</div>;
    }

    const tcInfo = tipoCambioInfo || (() => {
        try { return config.tipo_cambio_info ? JSON.parse(config.tipo_cambio_info) : null; } catch (e) { return null; }
    })();

    // Parsea ISO ("2026-06-18T21:48:38") o formato peruano localizado
    // ("18/6/2026, 9:48:38 p. m."). Devuelve Date valida o null.
    const parseFecha = (val) => {
        if (!val) return null;
        let d = new Date(val);
        if (!isNaN(d.getTime())) return d;
        // Fallback: regex para formato "DD/M/YYYY[, HH:MM[:SS] [a.m./p.m.]]"
        const m = String(val).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(a\.?\s*m\.?|p\.?\s*m\.?)?)?$/i);
        if (!m) return null;
        let [, dd, mm, yyyy, hh, mi, ss, ampm] = m;
        hh = hh ? parseInt(hh, 10) : 0;
        if (ampm && /p/i.test(ampm) && hh < 12) hh += 12;
        if (ampm && /a/i.test(ampm) && hh === 12) hh = 0;
        d = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd), hh, parseInt(mi || 0), parseInt(ss || 0));
        return isNaN(d.getTime()) ? null : d;
    };

    // "18 Jun. 2026 21:48" — usado para el actualizado de SUNAT.
    const formatearFechaHora = (val) => {
        const d = parseFecha(val);
        if (!d) return val || '';
        const mes = d.toLocaleDateString('es-PE', { month: 'short' }).replace(/\./g, '');
        const mesCap = mes.charAt(0).toUpperCase() + mes.slice(1);
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${d.getDate()} ${mesCap}. ${d.getFullYear()} ${hh}:${mm}`;
    };

    return (
        <div className="flex flex-col gap-4">
            {/* Header de la pagina */}
            <div className="flex flex-col">
                <h2 style={{ margin: 0 }}>Configuración</h2>
                <span className="text-sm text-muted mt-1">
                    Parámetros operativos: tipo de cambio, IGV, rentabilidad e integraciones. La numeración se gestiona desde <strong>Numeraciones</strong>.
                </span>
            </div>

            {/* Fila: Tipo de cambio + IGV/Rentabilidad en 2 columnas (desktop) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Tipo de cambio */}
                <Card>
                    <Card.Header title="Tipo de cambio" />
                    <Card.Body>
                        <div className="flex flex-col gap-3">
                            <Input
                                id="cfg-tc"
                                label="PEN por 1 USD"
                                type="number"
                                step="0.01"
                                min="0"
                                value={config.tipo_cambio || '3.70'}
                                onChange={(e) => handleLocalChange('tipo_cambio', e.target.value)}
                            />
                            <Button
                                variant="soft"
                                size="sm"
                                leftIcon={<RefreshCw size={14} className={loadingTC ? 'spin' : ''} />}
                                onClick={actualizarTipoCambioSunat}
                                disabled={loadingTC}
                            >
                                {loadingTC ? 'Consultando…' : 'Actualizar desde SUNAT'}
                            </Button>
                            {tcInfo && (
                                <div className="text-[11px] text-muted flex flex-wrap gap-x-3 gap-y-1 pt-1">
                                    {tcInfo.compra != null && <span>Compra: <strong>{tcInfo.compra}</strong></span>}
                                    {tcInfo.venta != null && <span>Venta: <strong>{tcInfo.venta}</strong></span>}
                                    {tcInfo.actualizado && (
                                        <span className="basis-full">
                                            Actualizado al: {formatearFechaHora(tcInfo.actualizado)}
                                        </span>
                                    )}
                                    {tcInfo.fuente && <span>Fuente: {tcInfo.fuente}</span>}
                                </div>
                            )}
                        </div>
                    </Card.Body>
                </Card>

                {/* IGV + Rentabilidad — los 3 inputs apilados (entran cómodos en 33% del ancho) */}
                <Card>
                    <Card.Header title="IGV y Rentabilidad" />
                    <Card.Body>
                        <div className="flex flex-col gap-3">
                            <Input
                                id="cfg-igv"
                                label="Tasa de IGV (%)"
                                type="number"
                                step="0.01"
                                min="0"
                                value={toPercentInput(config.igv_rate || '0.18')}
                                onChange={(e) => handleLocalChange('igv_rate', percentToDecimal(e.target.value).toString())}
                                hint="Por defecto: 18%"
                            />
                            <Input
                                id="cfg-rent-gral"
                                label="Rentabilidad general (%)"
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={toPercentInput(config.rentabilidad || '0.68')}
                                onChange={(e) => handleLocalChange('rentabilidad', percentToDecimal(e.target.value).toString())}
                                hint="Para servicios con personal"
                            />
                            <Input
                                id="cfg-rent-cf"
                                label="Rentabilidad costo fijo (%)"
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={toPercentInput(config.rentabilidad_costo_fijo || '0.68')}
                                onChange={(e) => handleLocalChange('rentabilidad_costo_fijo', percentToDecimal(e.target.value).toString())}
                                hint="Para costos fijos"
                            />
                        </div>
                    </Card.Body>
                </Card>

            </div>

            {/* Google Sheets */}
            <Card>
                <Card.Header title="Integración con Google Sheets" />
                <Card.Body>
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-muted">URL del Web App</label>
                        <SecretInput
                            hasValue={!!config.google_sheets_url}
                            preview={config.google_sheets_url}
                            onSave={(val) => handleImmediateSave('google_sheets_url', val)}
                            onClear={() => handleImmediateSave('google_sheets_url', '')}
                            placeholder="https://script.google.com/macros/s/..."
                        />
                        <p className="text-[11px] text-muted">
                            URL del Google Apps Script para sincronizar cotizaciones automáticamente.
                            Deja vacío para desactivar la sincronización.
                        </p>
                    </div>
                </Card.Body>
            </Card>

            {/* Slack */}
            <Card>
                <Card.Header title="Integración con Slack" />
                <Card.Body>
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-medium text-muted">Webhook URL</label>
                            <SecretInput
                                hasValue={!!slackConfig.webhook_url}
                                preview={slackConfig.webhook_url}
                                onSave={async (val) => {
                                    try {
                                        await alertasAPI.updateSlack({ webhook_url: val });
                                        // Recargar: el backend nos devuelve la version enmascarada con ultimos 4 chars.
                                        const nueva = await alertasAPI.getSlack();
                                        setSlackConfig(prev => ({ ...prev, webhook_url: nueva.data?.webhook_url || '' }));
                                        toast.success('Webhook de Slack guardado');
                                    } catch (err) {
                                        toast.error('Error: ' + err.message);
                                    }
                                }}
                                onClear={async () => {
                                    try {
                                        await alertasAPI.updateSlack({ webhook_url: '', activo: false });
                                        setSlackConfig({ webhook_url: '', activo: false, app_url: slackConfig.app_url });
                                        toast.success('Webhook de Slack eliminado');
                                    } catch (err) {
                                        toast.error('Error: ' + err.message);
                                    }
                                }}
                                placeholder="https://hooks.slack.com/services/T.../B.../xxx"
                            />
                            <p className="text-[11px] text-muted">Crea un Incoming Webhook en Slack y pega la URL aquí.</p>
                        </div>

                        <Input
                            id="cfg-app-url"
                            label="URL pública de la aplicación"
                            type="text"
                            value={slackConfig.app_url || ''}
                            onChange={(e) => setSlackConfig(prev => ({ ...prev, app_url: e.target.value }))}
                            placeholder="https://tudominio.com"
                            hint="Se usa para generar enlaces a cotizaciones en las alertas de Slack."
                        />

                        <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <input
                                    type="checkbox"
                                    checked={slackConfig.activo}
                                    onChange={(e) => setSlackConfig(prev => ({ ...prev, activo: e.target.checked }))}
                                />
                                <span>Activar notificaciones por Slack</span>
                            </label>
                            <Button
                                variant="soft"
                                size="sm"
                                leftIcon={<Send size={14} />}
                                disabled={loadingSlackTest || !slackConfig.webhook_url}
                                onClick={async () => {
                                    setLoadingSlackTest(true);
                                    try {
                                        await alertasAPI.testSlack();
                                        toast.success('Mensaje de prueba enviado a Slack');
                                    } catch (err) {
                                        toast.error('Error: ' + err.message);
                                    } finally {
                                        setLoadingSlackTest(false);
                                    }
                                }}
                            >
                                {loadingSlackTest ? 'Enviando…' : 'Probar conexión'}
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* Barra de accion al final */}
            <div className="flex justify-end gap-2 pt-2">
                <Button
                    variant="primary"
                    onClick={guardarTodo}
                    disabled={saving}
                >
                    {saving ? 'Guardando…' : 'Guardar cambios'}
                </Button>
            </div>
        </div>
    );
};

export default ConfiguracionPanel;
